import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ChevronLeft, ChevronRight, User } from 'lucide-react';

const OpinionList = () => {
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalOpinions: 0,
    pageSize: 10
  });

  // Fetch opinions when page changes
  useEffect(() => {
    fetchOpinions(pagination.currentPage);
  }, [pagination.currentPage]);

  // Function to fetch opinions from the API
  const fetchOpinions = async (page = 1) => {
    try {
      setLoading(true);
      
      // Only fetch published opinions for the public list
      const response = await fetch(`/api/opinions/list?page=${page}&status=published`);
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      setOpinions(data.opinions || []);
      setPagination(data.pagination || {
        currentPage: page,
        totalPages: 1,
        totalOpinions: 0,
        pageSize: 10
      });
      
      setError(null);
    } catch (err) {
      setError(`Failed to load opinions: ${err.message}`);
      setOpinions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
      
      // Scroll to top on mobile when changing pages
      if (window.innerWidth < 640) {
        window.scrollTo(0, 0);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return dateString;
    }
  };

  // Format date for mobile display (shorter format)
  const formatMobileDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return dateString;
    }
  };

  // Create URL-friendly slug from the opinion title
  const getOpinionSlug = (title, id) => {
    return `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-${id}`;
  };

  if (loading && opinions.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 px-4">
        <div className="text-red-500 font-medium mb-4">{error}</div>
        <button 
          onClick={() => fetchOpinions(1)} 
          className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 mobile-touch-target"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (opinions.length === 0) {
    return (
      <div className="text-center py-12 sm:py-20 px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-700 mb-3 sm:mb-4">No opinions available yet</h2>
        <p className="text-gray-500">Check back soon for new content!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 mx-auto max-w-4xl px-0 sm:px-4">
      {/* Opinions List */}
      <div className="space-y-4 sm:space-y-8">
        {opinions.map(opinion => (
          <div key={opinion.id} className="mobile-card bg-white shadow-md sm:shadow-lg overflow-hidden transition-all hover:shadow-lg">
            <Link to={`/o/${getOpinionSlug(opinion.title, opinion.id)}.html`}>
              {/* Mobile-first layout structure */}
              <div className="flex flex-col md:flex-row">
                {/* Image section - optimized for mobile */}
                {opinion.meta_image_url && (
                  <div className="w-full h-40 sm:h-48 md:h-auto md:w-1/3 lg:w-1/4 flex-shrink-0">
                    <img 
                      src={opinion.meta_image_url} 
                      alt={opinion.title}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                    />
                  </div>
                )}
                
                <div className="p-4 sm:p-6 md:flex-1">
                  {/* Mobile-optimized heading */}
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 hover:text-slate-700 transition-colors">
                    {opinion.title}
                  </h2>
                  
                  {/* Author info - mobile-optimized layout */}
                  <div className="flex flex-wrap items-center text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 gap-x-3 sm:gap-x-4 gap-y-1 py-2 px-2 sm:px-3 bg-gray-50 rounded-md border border-gray-100">
                    <div className="flex items-center">
                      <User size={14} className="mr-1 text-gray-500" />
                      <span className="font-medium">{opinion.author}</span>
                    </div>
                    
                    {/* Different date format on mobile */}
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1 text-gray-500" />
                      <span className="hidden sm:inline">{formatDate(opinion.publication_date)}</span>
                      <span className="sm:hidden">{formatMobileDate(opinion.publication_date)}</span>
                    </div>
                    
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1 text-gray-500" />
                      <span>{opinion.reading_time_minutes} min read</span>
                    </div>
                  </div>
                  
                  {/* Subtle border - consistent on mobile and desktop */}
                  <div className="border-t border-gray-100 my-2 sm:my-3"></div>
                  
                  {/* Content preview - fewer lines on mobile */}
                  <div className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base line-clamp-3 sm:line-clamp-5 leading-snug sm:leading-relaxed">
                    {opinion.content_snippet.replace(/<[^>]*>?/gm, '')}
                  </div>
                  
                  {/* Styled Read More button - touch-friendly on mobile */}
                  <div className="inline-block bg-slate-100 hover:bg-slate-200 text-blue-600 text-xs sm:text-sm font-medium py-1.5 px-3 rounded border border-slate-300 transition-colors mobile-touch-target">
                    Read more →
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Mobile-optimized Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-between items-center border-t border-gray-200 pt-3 sm:pt-4 mt-6 sm:mt-8 px-4 sm:px-0">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className={`flex items-center space-x-1 px-3 py-2 rounded mobile-touch-target ${
              pagination.currentPage === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline">Previous</span>
          </button>
          
          <span className="text-xs sm:text-sm text-gray-700">
            {pagination.currentPage} / {pagination.totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`flex items-center space-x-1 px-3 py-2 rounded mobile-touch-target ${
              pagination.currentPage === pagination.totalPages
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default OpinionList;